import { Box, IconButton, Stack, Slider, Typography } from "@mui/material";
import { SkipPrevious, SkipNext } from "@mui/icons-material";
import { useRef, useState, useEffect, useCallback } from "react";

type Props = {
  src: string;
  "data-testid"?: string;
  autoplay?: boolean;
  muted?: boolean;
};

const VideoBox = ({ src, "data-testid": testId, muted = false }: Props) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [currentFrame, setCurrentFrame] = useState(0);
  const [totalFrames, setTotalFrames] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const frameTime = 1 / 0.5; // Assuming 0.5fps, adjust as needed

  // RAF-throttled seek to avoid spamming the media element while dragging
  const seekRafId = useRef<number | null>(null);
  const pendingSeekTimeRef = useRef<number | null>(null);

  const performSeek = useCallback((seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    const anyVideo = video as unknown as { fastSeek?: (t: number) => void };
    if (typeof anyVideo.fastSeek === "function") {
      try {
        anyVideo.fastSeek(seconds);
        return;
      } catch {
        // fall back below
      }
    }
    video.currentTime = seconds;
  }, []);

  const scheduleSeek = useCallback(
    (seconds: number) => {
      pendingSeekTimeRef.current = seconds;
      if (seekRafId.current !== null) return;
      seekRafId.current = requestAnimationFrame(() => {
        seekRafId.current = null;
        const target = pendingSeekTimeRef.current;
        if (target !== null) {
          performSeek(target);
        }
      });
    },
    [performSeek],
  );

  useEffect(() => {
    return () => {
      if (seekRafId.current !== null) {
        cancelAnimationFrame(seekRafId.current);
        seekRafId.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const updateFrameInfo = () => {
      if (video.duration && !isNaN(video.duration)) {
        setTotalFrames(Math.floor(video.duration / frameTime));
        // Only update current frame if not dragging to prevent conflicts
        if (!isDragging) {
          setCurrentFrame(Math.floor(video.currentTime / frameTime));
        }
      }
    };

    const handleLoadedMetadata = () => {
      updateFrameInfo();
    };

    const handleTimeUpdate = () => {
      updateFrameInfo();
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("timeupdate", handleTimeUpdate);

    return () => {
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [frameTime, isDragging]);

  const stepFrame = (direction: "forward" | "backward") => {
    const video = videoRef.current;
    if (!video) return;

    if (direction === "forward") {
      video.currentTime = Math.min(
        video.duration,
        video.currentTime + frameTime,
      );
    } else {
      video.currentTime = Math.max(0, video.currentTime - frameTime);
    }
  };

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    if (typeof newValue !== "number") return;

    const frameNumber = newValue;
    setCurrentFrame(frameNumber);
    setIsDragging(true);

    scheduleSeek(frameNumber * frameTime);
  };

  const handleSliderChangeCommitted = () => {
    setIsDragging(false);
    if (typeof currentFrame === "number") {
      // ensure final position is applied immediately
      performSeek(currentFrame * frameTime);
    }
  };

  return (
    <Stack>
      <Box
        data-testid={testId}
        sx={{
          display: "flex",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <video
          ref={videoRef}
          src={src}
          controls={false}
          autoPlay={false}
          muted={muted}
          playsInline
          preload="auto"
          style={{
            display: "flex",
            flex: 1,
            width: 1,
            height: "100%",
            maxHeight: "512px",
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: 1,
          px: 2,
          backgroundColor: "background.paper",
        }}
      >
        {/* Frame Counter */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Frame {currentFrame} of {totalFrames}
        </Typography>

        {/* Frame Slider */}
        <Box sx={{ width: "100%", mb: 1 }}>
          <Slider
            value={currentFrame}
            min={0}
            max={totalFrames}
            step={1}
            onChange={handleSliderChange}
            onChangeCommitted={handleSliderChangeCommitted}
            onMouseDown={() => setIsDragging(true)}
            onTouchStart={() => setIsDragging(true)}
            data-testid="frame-slider"
            sx={{
              color: "primary.main",
              "& .MuiSlider-thumb": {
                width: 16,
                height: 16,
              },
              "& .MuiSlider-track": {
                height: 4,
              },
              "& .MuiSlider-rail": {
                height: 4,
              },
            }}
          />
        </Box>

        {/* Frame Navigation Buttons */}
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            onClick={() => stepFrame("backward")}
            size="small"
            data-testid="frame-backward"
            title="Previous frame"
            disabled={currentFrame <= 0}
          >
            <SkipPrevious />
          </IconButton>
          <IconButton
            onClick={() => stepFrame("forward")}
            size="small"
            data-testid="frame-forward"
            title="Next frame"
            disabled={currentFrame >= totalFrames}
          >
            <SkipNext />
          </IconButton>
        </Stack>
      </Box>
    </Stack>
  );
};

export default VideoBox;
